type Point = { x: number; y: number };
type Footprint = { id: string; points: number[][] };

/** Find an interior point, including in concave rooms where a bbox centre can be outside. */
export function getInteriorPoint(points: number[][]): Point | null {
  if (points.length < 3 || points.some(p => !Number.isFinite(p[0]) || !Number.isFinite(p[1]))) return null;
  const ys = [...new Set(points.map(p => p[1]))].sort((a, b) => a - b);
  let best: Point | null = null;
  let bestClearance = 0;
  for (let row = 1; row < ys.length; row++) {
    const height = ys[row] - ys[row - 1];
    const y = ys[row - 1] + height / 2;
    const crossings: number[] = [];
    for (let i = 0; i < points.length; i++) {
      const a = points[i], b = points[(i + 1) % points.length];
      if ((a[1] > y) !== (b[1] > y)) {
        crossings.push(a[0] + (y - a[1]) * (b[0] - a[0]) / (b[1] - a[1]));
      }
    }
    crossings.sort((a, b) => a - b);
    for (let i = 0; i + 1 < crossings.length; i += 2) {
      const width = crossings[i + 1] - crossings[i];
      const clearance = Math.min(width, height);
      if (clearance > bestClearance) {
        bestClearance = clearance;
        best = { x: crossings[i] + width / 2, y };
      }
    }
  }
  return best;
}

/** Footprints and result use original model coordinates, before viewer rotation/axis flip. */
export function getStreetViewStartPosition(
  footprints: Footprint[],
  preferredId?: string | null,
  near?: Point | null,
): Point | null {
  const candidates = footprints.flatMap(footprint => {
    const point = getInteriorPoint(footprint.points);
    return point ? [{ id: footprint.id, point }] : [];
  });
  const selected = candidates.find(candidate => candidate.id === preferredId);
  if (selected) return selected.point;
  if (!candidates.length) return null;
  // Median avoids isolated model outliers pulling the initial position away from the building.
  const median = (values: number[]) => values.sort((a, b) => a - b)[Math.floor(values.length / 2)];
  const target = near ?? {
    x: median(candidates.map(c => c.point.x)),
    y: median(candidates.map(c => c.point.y)),
  };
  return candidates.reduce((best, candidate) =>
    Math.hypot(candidate.point.x - target.x, candidate.point.y - target.y) <
    Math.hypot(best.point.x - target.x, best.point.y - target.y) ? candidate : best
  ).point;
}

/** Resolve the room under the marker on the active plan; prefer a room over a containing zone. */
export function getStreetViewRoom<T extends Footprint>(rooms: T[], position: Point | null): T | null {
  if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.y)) return null;
  let match: T | null = null;
  let smallestArea = Infinity;
  for (const room of rooms) {
    const points = room.points;
    if (points.length < 3 || points.some(p => !Number.isFinite(p[0]) || !Number.isFinite(p[1]))) continue;
    let inside = false;
    let onBoundary = false;
    let twiceArea = 0;
    for (let i = 0; i < points.length; i++) {
      const a = points[i], b = points[(i + 1) % points.length];
      twiceArea += a[0] * b[1] - b[0] * a[1];
      const dx = b[0] - a[0], dy = b[1] - a[1];
      const cross = (position.x - a[0]) * dy - (position.y - a[1]) * dx;
      if (Math.abs(cross) <= 1e-8 * Math.hypot(dx, dy) &&
        position.x >= Math.min(a[0], b[0]) && position.x <= Math.max(a[0], b[0]) &&
        position.y >= Math.min(a[1], b[1]) && position.y <= Math.max(a[1], b[1])) onBoundary = true;
      if ((a[1] > position.y) !== (b[1] > position.y) &&
        position.x < a[0] + (position.y - a[1]) * dx / dy) inside = !inside;
    }
    const area = Math.abs(twiceArea);
    if ((inside || onBoundary) && area > 0 && area < smallestArea) {
      match = room;
      smallestArea = area;
    }
  }
  return match;
}

/** Presentation label shared by standalone viewers without depending on application Redux. */
export function getStreetViewRoomLabel(
  footprints: Array<Footprint & { text?: { values?: string[] } }> | undefined,
  position: { x: number; y: number } | null,
): string {
  const room = getStreetViewRoom(footprints ?? [], position);
  return room
    ? [...new Set([room.id, room.text?.values?.[1]].filter(value => value && value !== "-"))].join(" · ")
    : "Utenfor registrerte rom";
}
